import React, { useEffect, useState } from "react";
import moment from "moment";
import { Container, Form, Button, Table, Spinner, Badge } from "react-bootstrap";
import ServiceDetail from "../components/ServiceDetail.tsx";
import ServiceApiService from "../services/service.service";
import UserService from "../services/user.service";
import type { ServiceItem, ServiceStatus } from "./models";

type ServiceListDisplayStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

const isServiceListDisplayStatus = (
  status: ServiceStatus,
): status is ServiceListDisplayStatus =>
  [
    "scheduled",
    "confirmed",
    "paid",
    "done",
    "client_not_show_up",
    "canceled_rob",
    "canceled_client",
    "canceled_overtime",
  ].includes(status);

const getStatusLabel = (
  status: ServiceStatus,
  companyShortName: string,
) => ({
  scheduled: "Scheduled",
  confirmed: "Confirmed",
  paid: "Paid",
  done: "Done",
  client_not_show_up: "No-show",
  canceled_rob: `Canceled (${companyShortName})`,
  canceled_client: "Canceled (client)",
  canceled_overtime: "Canceled (overtime)",
} satisfies Record<ServiceListDisplayStatus, string>);

const statusVariant: Record<ServiceListDisplayStatus, string> = {
  scheduled: "warning",
  confirmed: "danger",
  paid: "success",
  done: "info",
  client_not_show_up: "dark",
  canceled_rob: "secondary",
  canceled_client: "secondary",
  canceled_overtime: "secondary",
};

const ServiceList = () => {
  const [showDetail, setShowDetail] = useState(false);
  const [clientName, setClientName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [companyShortName, setCompanyShortName] = useState("RDS");
  const [services, setServices] = useState<ServiceItem[] | undefined>(undefined);
  const [currentService, setCurrentService] = useState<ServiceItem | undefined>(undefined);

  useEffect(() => {
    const loadAccountSettings = async () => {
      try {
        const result = await UserService.getAccountSettings();
        setCompanyShortName(result.data.data?.companyShortName ?? "RDS");
      } catch (err) {
        console.log(err);
      }
    };

    loadAccountSettings();
  }, []);

  const search = async () => {
    setIsLoading(true);
    try {
      const result = await ServiceApiService.getServicesByClient(clientName);
      setServices(result.data.data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container className="mt-4">
        <h3 className="mb-4">Service Appointments</h3>
        <Form className="mb-4">
          <Form.Group className="mb-3">
            <Form.Label>Search by client name or phone</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter name or phone number"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </Form.Group>
          <div className="d-flex justify-content-center">
            <Button
              variant="primary"
              style={{ minWidth: 200 }}
              onClick={search}
              disabled={isLoading}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : "Search"}
            </Button>
          </div>
        </Form>

        {services && (
          <Table striped hover responsive>
            <thead className="table-dark">
              <tr>
                <th>Client</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Duration</th>
                <th>Technician</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr
                  key={s.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setCurrentService(s);
                    setShowDetail(true);
                  }}
                >
                  <td>{s.client?.clientName}</td>
                  <td>{s.client?.phoneNumber}</td>
                  <td>{moment(s.startDateTime).format("LLL")}</td>
                  <td>{s.durationHours}h</td>
                  <td>{s.technician?.name || "—"}</td>
                  <td>
                    <Badge
                      bg={
                        isServiceListDisplayStatus(s.status)
                          ? statusVariant[s.status]
                          : "secondary"
                      }
                    >
                      {isServiceListDisplayStatus(s.status)
                        ? getStatusLabel(s.status, companyShortName)[s.status]
                        : s.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>
      <ServiceDetail
        show={showDetail}
        onHide={() => setShowDetail(false)}
        service={currentService}
        companyShortName={companyShortName}
        onServiceChange={(updatedService) => {
          search();
          if (updatedService) {
            setCurrentService(updatedService);
          }
        }}
      />
    </>
  );
};

export default ServiceList;
